
import React from "react";
import { CooksyLogo } from "@/components/CooksyLogo";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format, subMonths, parseISO } from "date-fns";
import { EarningsSection } from "@/components/seller/business/EarningsSection";
import { ReviewsSection } from "@/components/seller/business/ReviewsSection";
import { PayoutsSection } from "@/components/seller/business/PayoutsSection";

interface Transaction {
  id: string;
  amount: number;
  buyer_id: string;
  chef_id: string;
  chef_payout: number;
  created_at: string;
  meal_id: string;
  order_id: string;
  payout_scheduled_date: string | null;
  payout_status: 'pending' | 'scheduled' | 'completed';
  platform_fee: number;
  refund_amount: number | null;
  refund_reason: string | null;
  refund_status: string | null;
  status: string;
  stripe_fee: number;
  stripe_payment_intent_id: string | null;
  stripe_transfer_id: string | null;
  updated_at: string;
}

const SellerBusiness = () => {
  const navigate = useNavigate();

  // Fetch chef's transactions
  const { data: transactions, isLoading: isLoadingTransactions, refetch: refetchTransactions } = useQuery({
    queryKey: ['chef-transactions'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('chef_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      console.log(`Fetched ${data.length} transactions for chef ${user.id}`);
      
      // Cast the payout_status to the expected type
      return data.map((transaction) => ({
        ...transaction,
        payout_status: transaction.payout_status as 'pending' | 'scheduled' | 'completed'
      })) as Transaction[];
    },
    retry: 2,
    retryDelay: 1000
  });

  // Fetch chef's reviews
  const { data: reviews, isLoading: isLoadingReviews } = useQuery({
    queryKey: ['chef-reviews'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('reviews')
        .select(`
          *,
          meal:meals(name, chef_id),
          user:profiles(first_name, last_name)
        `)
        .eq('meal.chef_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    }
  });

  // Format number with commas
  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  // Improved chart data preparation to ensure accurate monthly aggregation
  const chartData = React.useMemo(() => {
    if (!transactions || transactions.length === 0) return [];
    
    // Create a map of all months in the last 6 months for consistent display
    const lastSixMonths = Array.from({ length: 6 }, (_, i) => {
      const date = subMonths(new Date(), i);
      return format(date, 'MMM yyyy');
    }).reverse();
    
    // Initialize the monthly data map with all months
    const monthlyData = new Map();
    
    lastSixMonths.forEach(date => {
      monthlyData.set(date, {
        date,
        pending: 0,
        scheduled: 0,
        completed: 0,
        amount: 0
      });
    });
    
    // Now populate with actual transaction data
    transactions.forEach(tx => {
      // Only process completed transactions for payment stats
      if (tx.status === 'completed') {
        const date = format(new Date(tx.created_at), 'MMM yyyy');
        
        // Skip if the date is not in our 6-month window
        if (!monthlyData.has(date)) return;
        
        const monthEntry = monthlyData.get(date);
        
        // Add amount based on payout status - only count each transaction once
        const payoutAmount = tx.chef_payout || 0;
        
        if (tx.payout_status === 'pending') {
          monthEntry.pending += payoutAmount;
        } else if (tx.payout_status === 'scheduled') {
          monthEntry.scheduled += payoutAmount;
        } else if (tx.payout_status === 'completed') {
          monthEntry.completed += payoutAmount;
        }
        
        // The total amount is the sum of all statuses
        monthEntry.amount += payoutAmount;
      }
    });
    
    // Convert map to array and sort by date
    return Array.from(monthlyData.values())
      .sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateA.getTime() - dateB.getTime();
      });
  }, [transactions]);

  return (
    <div className="min-h-screen bg-background pb-24">
      <main className="container mx-auto px-4 py-6">
        <Tabs defaultValue="earnings" className="w-full">
          <TabsList className="w-full justify-start mb-6">
            <TabsTrigger value="earnings" className="flex-1 text-black">Earnings</TabsTrigger>
            <TabsTrigger value="payouts" className="flex-1 text-black">Payouts</TabsTrigger>
            <TabsTrigger value="reviews" className="flex-1 text-black">Reviews</TabsTrigger>
          </TabsList>

          <TabsContent value="earnings">
            <EarningsSection
              transactions={transactions}
              chartData={chartData}
              formatNumber={formatNumber}
              isLoading={isLoadingTransactions}
            />
          </TabsContent>
          
          <TabsContent value="payouts">
            <PayoutsSection formatNumber={formatNumber} />
          </TabsContent>

          <TabsContent value="reviews">
            <ReviewsSection
              reviews={reviews}
              isLoading={isLoadingReviews}
            />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default SellerBusiness;
